import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { IDentalChart } from '@dataTypes/chartingFeature.types'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import {
  isExplicitDentalHistorySelection,
  isPersistedDentalChart,
  resolveActiveDentalChart,
} from '@utils/active-dental-chart.utils'
import {
  setCreateDentalChart,
  setSelectedDentalChart,
  upsertDentalChartInList,
} from '@store/reducers/charting.reducer'

export interface UseActiveDentalChartReturn {
  activeDentalChart: IDentalChart
  isExplicitHistorySelection: boolean
  isPersistedChart: boolean
  showExistingConditions: boolean
  commitActiveDentalChartUpdate: (chart: IDentalChart) => void
}

export const useActiveDentalChart = (): UseActiveDentalChartReturn => {
  const dispatch = useAppDispatch()
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const preferCreateDentalChart = useAppSelector(
    (state) => state.charting.preferCreateDentalChart,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const { showExistingConditions } = useClinicChartingPreferences()

  const activeDentalChart = useMemo(
    (): IDentalChart =>
      resolveActiveDentalChart({
        selectedDentalChart,
        createDentalChart,
        dentalCharts,
        showExistingConditions,
        patientId: selectedPatient?.patient_id,
        preferCreateDentalChart,
      }),
    [
      selectedDentalChart,
      createDentalChart,
      dentalCharts,
      showExistingConditions,
      selectedPatient?.patient_id,
      preferCreateDentalChart,
    ],
  )

  const isExplicitHistorySelection = useMemo(
    (): boolean =>
      isExplicitDentalHistorySelection(selectedDentalChart, createDentalChart),
    [selectedDentalChart, createDentalChart],
  )

  const isPersistedChart = useMemo(
    (): boolean => isPersistedDentalChart(activeDentalChart, createDentalChart),
    [activeDentalChart, createDentalChart],
  )

  const commitActiveDentalChartUpdate = useCallback(
    (chart: IDentalChart): void => {
      if (isExplicitHistorySelection) {
        dispatch(setSelectedDentalChart(chart))
        dispatch(upsertDentalChartInList(chart))
        return
      }

      if (isPersistedChart) {
        dispatch(upsertDentalChartInList(chart))
        return
      }

      dispatch(setCreateDentalChart(chart))
    },
    [dispatch, isExplicitHistorySelection, isPersistedChart],
  )

  return {
    activeDentalChart,
    isExplicitHistorySelection,
    isPersistedChart,
    showExistingConditions,
    commitActiveDentalChartUpdate,
  }
}
